import { FC } from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import { getColor } from "../../../helpers/utils";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useTranslation } from "react-i18next";
import { ChartData } from "../../../types/chartData";
import { ChartOptions } from "chart.js/auto";

type ActivityComparisonBarChartProps = {
  height: string;
  showPcLabel: boolean;
  selected1: string;
  selected2: string | null;
  attribute: string;
  data1: ChartData;
  data2: ChartData | null;
  pc1: string;
  pc2: string | null;
};

const ActivityComparisonBarChart: FC<ActivityComparisonBarChartProps> = ({
  height,
  showPcLabel,
  selected1,
  selected2,
  attribute,
  data1,
  data2,
  pc1,
  pc2,
}) => {
  const { t } = useTranslation();
  let projectCode1 = data1 === undefined ? t("unavailable") : pc1;
  let projectCode2 = data2 === undefined ? t("unavailable") : pc2;

  if (projectCode1 === projectCode2) {
    projectCode1 = projectCode1 + " (1)";
    projectCode2 = projectCode2 + " (2)";
  }
  let activityData1 = data1.activities
    .filter((activity) => activity.average !== null)
    .map((activity, index) => ({
      name: index == 0 ? t(`${activity.activityName}`) : activity.activityName,
      value:
        attribute === "MLI"
          ? activity.average?.mli
          : attribute === t("pain")
          ? activity.average?.pain
          : activity.average?.mli,
    }));

  var labels1: string[] = [];
  var values1: number[] = [];
  var backgroundColors1: string[] = [];

  activityData1.forEach((activity) => {
    labels1.push(activity.name + (showPcLabel ? " - " + projectCode1 : ""));
    if (activity.value) values1.push(activity.value);
    backgroundColors1.push(getColor(activity, selected1, t, attribute));
  });

  if (data2) {
    let activityData2 = data2.activities
      .filter((activity) => activity.average !== null)
      .map((activity, index) => ({
        name:
          index == 0 ? t(`${activity.activityName}`) : activity.activityName,
        value:
          attribute === "MLI"
            ? activity.average?.mli
            : attribute === t("pain")
            ? activity.average?.pain
            : activity.average?.mli,
      }));

    var labels2: string[] = [];
    var values2: number[] = [];
    var backgroundColors2: string[] = [];
    activityData2.forEach((activity) => {
      labels2.push(activity.name + (showPcLabel ? " - " + projectCode2 : ""));
      if (activity.value) values2.push(activity.value);
      backgroundColors2.push(getColor(activity, selected2, t, attribute));
    });

    labels1 = labels1.concat(labels2);
    values1 = values1.concat(values2);
    backgroundColors1 = backgroundColors1.concat(backgroundColors2);
  }

  const data = {
    labels: labels1,
    datasets: [
      {
        label: "", // Keep this empty if you don't want labels
        data: values1,
        backgroundColor: backgroundColors1,
        borderColor: backgroundColors1,
        borderWidth: 1,
      },
    ],
  };

  const options: ChartOptions<"bar"> = {
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 10,
      },
    },

    plugins: {
      title: {
        display: true,
        text: `   ${attribute} ${t("per_activity")}`,
        align: "start",
        padding: {
          top: 5,
          bottom: 15,
        },
      },
      legend: {
        display: false,
      },
      datalabels: {
        display: false, // This will hide the data labels
      },
    },
    // Additional options can be added here
  };

  return (
    <div
      style={{
        height: height,
        width: "99%",
      }}
    >
      <Bar plugins={[ChartDataLabels]} data={data} options={options} />
    </div>
  );
};

export default ActivityComparisonBarChart;
